// @flow

import React, { useEffect, useState, useRef } from "react";
import { localize } from "redux-i18n";
import Ajv from "ajv";

import { split } from "@transformers/schema";
import actionCreators from "@modules/service/actionCreator";
import { Button, View } from "@components";
import type { StepProps } from "@components";
import { EmailNotificationDistributionRulesForm } from "@modules/service/components";
import { EmailDistributionRulesEnum } from "@modules/service/constants";

type OwnProps = $Exact<{
  editing: boolean,
  edited: Object,
  schema: Object,
  default: boolean,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...StepProps,
  ...PropsFromLocalize,
}>;

export function Step3Rules(props: Props) {
  const [data, setData] = useState(props.data);
  const [schema, setSchema] = useState(
    split(props.schema, [
      "emailDistributionType",
      "batchedEmailFrequency",
      "includeRtwDetailsInAbsenceNotifications",
      "includeAlertsInAbsenceNotifications",
    ]),
  );

  function getData() {
    const formData = Object.entries(props.edited).reduce(
      (acc: Object, [k, v]: [string, mixed]) =>
        k === "reasonSpecificInstructions" ? acc : { ...acc, [k]: v },
      {},
    );

    return {
      ...formData,
      emailDistributionType: EmailDistributionRulesEnum.AFTER_EVERY_ABSENCE, // AU-10225 FE should send only AFTER_EVERY_ABSENCE distribution type
      batchedEmailFrequency: null,
    };
  }

  function handleSubmit() {
    props.handleSubmit(
      {
        emailDistributionType: data.emailDistributionType,
        batchedEmailFrequency: data.batchedEmailFrequency,
        includeRtwDetailsInAbsenceNotifications: data.includeRtwDetailsInAbsenceNotifications,
        includeAlertsInAbsenceNotifications: data.includeAlertsInAbsenceNotifications,
      },
      true,
    );
  }

  function handleChange(formData: Object) {
    setData(formData);
    props.onChange(
      {
        emailDistributionType: formData.emailDistributionType || undefined,
        batchedEmailFrequency: formData.batchedEmailFrequency,
        includeRtwDetailsInAbsenceNotifications: formData.includeRtwDetailsInAbsenceNotifications,
        includeAlertsInAbsenceNotifications: formData.includeAlertsInAbsenceNotifications,
      },
      true,
    );
  }

  function isValid() {
    const validate = new Ajv().compile(schema);
    return validate && validate(data);
  }

  if (!props.editable) {
    return <div />;
  }

  return (
    <View flex={1} gap={4}>
      {schema && data && (
        <View className="gs-container">
          <EmailNotificationDistributionRulesForm
            data={getData()}
            schema={props.schema}
            onChange={handleChange}
            onSubmit={handleSubmit}
          />
        </View>
      )}
      {props.editing || props.default ? null : (
        <View direction="row" justifyContent="flex-end">
          <Button
            materialIcon="fast_forward"
            onClick={handleSubmit}
            disabled={!isValid()}
            label={props.t("NEXT")}
          />
        </View>
      )}
    </View>
  );
}

export default localize()(Step3Rules);
